import { useContext, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'

import { useChannels } from 'src/components/layouts/hooks/useChannel'
import PropTypes from 'prop-types'
import styled from 'styled-components'

import { MAIN_PATHS } from 'src/routes/main'
import { MENU_PATHS } from 'src/routes/menu'
import { HistoryRouteContext } from 'src/routes'

import useNewsListChannel from 'src/services/common/useNewsListChannel'
import useUserContactListChannel from 'src/services/common/useUserContactChannel'
import useNewsHasNotifications from 'src/hooks/useNewsHasNotifications'
import NotificationBadge from 'src/components/common/NotificationBadge'
import { hasNotif } from 'src/lib/notification'

import iconMypage from 'src/assets/img/footer/mypage.svg'
import iconActiveMypage from 'src/assets/img/footer/mypage_active.svg'
import iconTalk from 'src/assets/img/footer/talk.svg'
import iconActiveTalk from 'src/assets/img/footer/talk_active.svg'
import iconMenu from 'src/assets/img/footer/menu.png'
import iconActiveMenu from 'src/assets/img/footer/menu_active.png'
import iconGacha from 'src/assets/img/footer/gacha.svg'
import iconActiveGacha from 'src/assets/img/footer/gacha_active.svg'
import iconShop from 'src/assets/img/footer/shop.svg'
import iconActiveShop from 'src/assets/img/footer/shop_active.svg'
import iconHouse from 'src/assets/img/footer/house.svg'
import iconActiveHouse from 'src/assets/img/footer/house_active.svg'

import { useAuthState } from 'src/providers/AuthProviders'
import useConversationListChannel from 'src/services/common/useConversationListChannel'
import { roomType } from 'src/constants/roomTypeList'

export const Footer = ({ active }) => {
  const location = useLocation()
  const { redirectTo } = useContext(HistoryRouteContext)
  const { user } = useAuthState()
  const { data: singleChatChannelList, refetch: refetchStory } = useChannels(user.id)
  const { data: realtimeChatChannelList, refetch: refetchRealtime } = useChannels(
    user.id,
    roomType.REALTIME
  )
  const [hasUnreadMessage, setHasUnreadMessage] = useState(false)
  const { message } = useConversationListChannel()
  const isNewMessageInquiry = useSelector((state) => state.getNotifications.isNewMessageInquiry)
  const inquiryNoti = useSelector((state) => state.getNotifications.inquiryNoti)
  const hasNews = useSelector((state) => state.getNotifications.hasNews)
  const hasNewsNotifications = useSelector((state) => state.getNotifications.hasNewsNotifications)
  const prevLocation = useSelector((state) => state.historyRoute.locations)
  const buttons = [
    {
      label: 'マイページ',
      icon: iconMypage,
      active: iconActiveMypage,
      cat: 'mypage',
      goto: MAIN_PATHS.MyPage
    },
    {
      label: 'ガチャ',
      icon: iconGacha,
      active: iconActiveGacha,
      cat: 'gacha',
      goto: MAIN_PATHS.CapsuleToyPage
    },
    {
      label: 'ショップ',
      icon: iconShop,
      active: iconActiveShop,
      cat: 'shop',
      goto: MAIN_PATHS.ShopPage
    },
    {
      label: 'トーク',
      icon: iconTalk,
      active: iconActiveTalk,
      cat: 'talk',
      goto: MAIN_PATHS.RealtimePage
    },
    {
      label: 'ハウス',
      icon: iconHouse,
      active: iconActiveHouse,
      cat: 'house',
      goto: MAIN_PATHS.HousePage
    },
    {
      label: 'メニュー',
      icon: iconMenu,
      active: iconActiveMenu,
      cat: 'menu',
      goto: MENU_PATHS.Menu
    }
  ]

  useEffect(() => {
    let unreadSingleChat
    let unreadRealtimeChat
    if (singleChatChannelList) {
      unreadSingleChat = hasNotif(singleChatChannelList || [])
    }
    if (realtimeChatChannelList) {
      unreadRealtimeChat = hasNotif(realtimeChatChannelList || [])
    }
    setHasUnreadMessage(unreadSingleChat || unreadRealtimeChat)
  }, [singleChatChannelList, realtimeChatChannelList])

  useEffect(() => {
    if (message?.conversation_id && message?.unreadTotal > 0) {
      const matchingChat = realtimeChatChannelList.find(
        (chatItem) => chatItem?.conversation_id === message.conversation_id
      )
      setHasUnreadMessage(!matchingChat || matchingChat.character.id !== 6)
    }
  }, [message, realtimeChatChannelList])

  useEffect(() => {
    if (prevLocation.length > 0) {
      if (prevLocation[0].pathname.startsWith('/talk_real/detail/')) refetchRealtime()
      if (prevLocation[0].pathname.startsWith('/talk_story/detail/')) refetchStory()
    }
  }, [prevLocation])

  useEffect(() => {
    if (location.pathname === MAIN_PATHS.RealtimePage) refetchRealtime()
    if (location.pathname === MAIN_PATHS.MessagePage) refetchStory()
  }, [location])

  useNewsListChannel()
  useUserContactListChannel()
  useNewsHasNotifications()

  return (
    <ul
      className="max-h-footer-offset fixed bottom-0 bg-cover bg-center bg-white grid grid-cols-6 z-50 shadow-[0_0_0_0.1px_#C5C5C5]"
      style={{ width: 'inherit' }}
    >
      {buttons.map((v, i) => {
        return (
          <ButtonFooter
            key={i}
            className={`text-xs text-center bg-cover bg-center relative py-4 cursor-pointer ${
              i === buttons.length - 1
                ? 'shadow-[0_0_0_0.1px_#C5C5C5,0_0_0_0.1px_#C5C5C5]'
                : 'shadow-[0_0_0_0.1px_#C5C5C5,0_0_0_0]'
            }`}
            onClick={() => redirectTo(v.goto)}
          >
            <img className="mx-auto h-[48px]" src={active === v.cat ? v.active : v.icon} />
            {v.cat === 'menu' &&
              (inquiryNoti || isNewMessageInquiry || hasNewsNotifications || hasNews) && (
              <NotificationBadge className="-top-1 right-0" />
            )}
            {v.cat === 'talk' && hasUnreadMessage && (
              <NotificationBadge className="-top-1 right-0" />
            )}
          </ButtonFooter>
        )
      })}
    </ul>
  )
}

const ButtonFooter = styled.li`
  @media (max-width: 395px) {
    padding-bottom: 1rem !important;
    p {
      font-size: 8px !important;
    }
  }
`

Footer.propTypes = {
  active: PropTypes.string
}
